import React from "react";
import { Button } from "@/components/ui/button";
import { getStatusOptionsForSelect } from "@/constants/requestConstants";
import { useHotel } from "@/hooks/useHotel";
import { useAllServices } from "@/hooks/useHotelServices";
import { useStaff } from "@/components/pages/staff/hooks/useStaff";
import { useTranslation } from "react-i18next";
import { DateRange } from "@/hooks/useDateRange";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import { RequestType } from "@/types/request";
import { useRequestsCreators } from "../hooks/useRequests";
import { FilterMultiSelect } from "@/components/common/FilterMultiSelect";

interface RequestFiltersProps {
  activeTab: RequestType;
  createdByFilter: string[];
  assignedStaffFilter: string[];
  dateRangeFilter: DateRange;
  statusFilter: string[];
  serviceFilter: string[];
  priorityFilter: boolean;
  onStatusChange: (values: string[]) => void;
  onServiceChange: (values: string[]) => void;
  onPriorityChange: (value: boolean) => void;
  onDateRangeChange: (value: DateRange) => void;
  onCreatedByChange: (value: string[]) => void;
  onAssignedStaffChange: (value: string[]) => void;
}

export const RequestFilters: React.FC<RequestFiltersProps> = ({
  createdByFilter,
  assignedStaffFilter,
  dateRangeFilter,
  statusFilter,
  serviceFilter,
  priorityFilter,
  onStatusChange,
  onServiceChange,
  onPriorityChange,
  onDateRangeChange,
  onCreatedByChange,
  onAssignedStaffChange,
  activeTab,
}) => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();

  const { data: services } = useAllServices(hotelId || "");
  const { data: creators } = useRequestsCreators();
  const { data: staffData } = useStaff({ hotelId: hotelId || "" });

  const staffOptions = React.useMemo(() => {
    return (
      staffData?.users.map((u) => ({
        value: u.staff?.id || "",
        label:
          `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "",
      })) || []
    );
  }, [staffData?.users]);

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-sm">
            {t("requests.filters.priority")}
          </span>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant={!priorityFilter ? "new_secondary" : "new_ghost"}
              size="sm"
              onClick={() => onPriorityChange(false)}
            >
              {t("requests.filters.all")}
            </Button>
            <Button
              type="button"
              variant={priorityFilter ? "new_secondary" : "new_ghost"}
              size="sm"
              onClick={() => onPriorityChange(true)}
            >
              {t("requests.filters.star")}
            </Button>
          </div>
        </div>
        {activeTab !== RequestType.ROOM && (
          <FilterMultiSelect
            className="min-w-[105px] justify-between"
            options={
              services?.data
                ? services.data.map((service) => ({
                    value: service.id,
                    label: service.name,
                  }))
                : []
            }
            placeholder={t("requests.filters.service")}
            value={serviceFilter}
            onValueChange={onServiceChange}
          />
        )}
        <FilterMultiSelect
          className="min-w-[105px] justify-between"
          options={getStatusOptionsForSelect()}
          placeholder={t("requests.filters.status")}
          value={statusFilter}
          onValueChange={onStatusChange}
        />
        <FilterMultiSelect
          className="min-w-[150px] justify-between"
          options={staffOptions}
          placeholder={t("requests.filters.assignedStaff")}
          value={assignedStaffFilter}
          onValueChange={onAssignedStaffChange}
        />
        <FilterMultiSelect
          className="min-w-[150px] justify-between"
          options={
            creators?.map((c) => ({
              value: c.id,
              label: `${c.firstName} ${c.lastName}`,
            })) || []
          }
          placeholder={t("requests.filters.createdBy")}
          value={createdByFilter}
          onValueChange={onCreatedByChange}
        />
        <DateRangePicker
          dateRange={dateRangeFilter}
          onDateRangeChange={onDateRangeChange}
          triggerVariant="new_outline"
        />
      </div>
    </div>
  );
};
